<template>
  <div class="wt-page">
    <header class="wt-hero">
      <div class="wt-heading">
        <div class="wt-heading-icon" aria-hidden="true"><v-icon size="24">mdi-clock-outline</v-icon></div>
        <div>
          <span class="wt-eyebrow">Czas pracy biura</span>
          <h1>Ewidencja czasu pracy</h1>
          <p>Czas liczony z aktywności w panelu, uzupełniany korektami.</p>
        </div>
      </div>
      <div class="wt-hero-actions">
        <div class="wt-context-card">
          <span><v-icon size="18">mdi-timer-outline</v-icon>dziś</span>
          <strong>{{ formatMinutes(todayMinutes) }}</strong>
          <small>{{ enabled ? 'ewidencja włączona' : 'ewidencja wyłączona' }}</small>
        </div>
        <button type="button" class="wt-refresh" :disabled="isLoading" @click="loadActiveTab">
          <v-icon size="19" :class="{ 'is-spinning': isLoading }">mdi-refresh</v-icon>
          <span>{{ isLoading ? 'Odświeżam…' : 'Odśwież dane' }}</span>
        </button>
      </div>
    </header>

    <v-alert v-if="!enabled" type="info" text dense class="wt-alert">
      Ewidencja jest wyłączona — panel nie zapisuje czasu pracy. Zanim ją włączysz, potrzebny
      jest zapis w regulaminie pracy albo obwieszczenie i poinformowanie pracowników
      (art. 22<sup>3</sup> Kodeksu pracy).
    </v-alert>
    <v-alert v-if="error" type="error" text dense class="wt-alert">{{ error }}</v-alert>

    <div class="wt-toolbar">
      <div class="wt-range">
        <span class="wt-toolbar-label"><v-icon size="17">mdi-calendar-range</v-icon> Zakres</span>
        <v-text-field v-model="from" type="date" label="Od" dense outlined hide-details class="wt-date" />
        <span class="wt-range-separator">—</span>
        <v-text-field v-model="to" type="date" label="Do" dense outlined hide-details class="wt-date" />
        <v-btn class="wt-show-btn" color="primary" :loading="isLoading" @click="loadActiveTab">Pokaż wyniki</v-btn>
      </div>
      <v-btn class="wt-correction-btn" outlined color="primary" @click="openCorrection()">
        <v-icon left size="19">mdi-plus-circle-outline</v-icon>Zgłoś korektę
      </v-btn>
    </div>

    <v-tabs v-model="tab" class="wt-tabs" show-arrows>
      <v-tab><v-icon left size="18">mdi-account-clock-outline</v-icon>Moja ewidencja</v-tab>
      <v-tab v-if="canSeeTeam"><v-icon left size="18">mdi-account-group-outline</v-icon>Zespół</v-tab>
      <v-tab v-if="canSeeTeam"><v-icon left size="18">mdi-clipboard-check-outline</v-icon>Wnioski<span v-if="pendingCorrections.length" class="wt-badge">{{ pendingCorrections.length }}</span></v-tab>
      <v-tab v-if="canConfigure"><v-icon left size="18">mdi-tune-variant</v-icon>Ustawienia</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab" class="wt-tab-body">
      <!-- ===== MOJA EWIDENCJA ===== -->
      <v-tab-item>
        <div class="wt-summary" v-if="mine.summary">
          <div><span class="wt-stat-icon"><v-icon size="20">mdi-calendar-check-outline</v-icon></span><small>Dni z zapisem</small><strong>{{ mine.summary.days }}</strong></div>
          <div><span class="wt-stat-icon"><v-icon size="20">mdi-timer-sand</v-icon></span><small>Z sesji</small><strong>{{ formatMinutes(mine.summary.sessionMinutes) }}</strong></div>
          <div><span class="wt-stat-icon"><v-icon size="20">mdi-pencil-outline</v-icon></span><small>Korekty</small><strong>{{ formatMinutes(mine.summary.correctionMinutes) }}</strong></div>
          <div class="wt-summary-total"><span class="wt-stat-icon"><v-icon size="20">mdi-clock-check-outline</v-icon></span><small>Łączny czas</small><strong>{{ formatMinutes(mine.summary.totalMinutes) }}</strong></div>
        </div>
        <div v-if="!mine.days.length && !isLoading" class="wt-empty"><span><v-icon size="28">mdi-calendar-blank-outline</v-icon></span><strong>Brak zapisów</strong><p>W wybranym zakresie nie zarejestrowano czasu pracy.</p></div>
        <v-expansion-panels v-else accordion class="wt-days">
          <v-expansion-panel v-for="day in mine.days" :key="day.date">
            <v-expansion-panel-header>
              <div class="wt-day-head">
                <span class="wt-day-calendar"><v-icon size="18">mdi-calendar-outline</v-icon></span>
                <strong>{{ formatDate(day.date) }}</strong>
                <span class="wt-day-total">{{ formatMinutes(day.totalMinutes) }}</span>
                <small v-if="day.correctionMinutes">w tym korekty {{ formatMinutes(day.correctionMinutes) }}</small>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <table class="wt-table">
                <thead><tr><th>Sesja</th><th>Koniec</th><th>Czas</th><th>Powód zamknięcia</th></tr></thead>
                <tbody>
                  <tr v-for="session in day.sessions" :key="session.id">
                    <td>{{ formatTime(session.startedAt) }}</td>
                    <td>{{ session.endedAt ? formatTime(session.endedAt) : '— trwa —' }}</td>
                    <td>{{ formatMinutes(session.minutes) }}</td>
                    <td>{{ endReasonLabel(session.endReason) }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-for="correction in day.corrections" :key="correction.id" class="wt-correction">
                <v-chip x-small :color="statusColor(correction.status)" dark>{{ statusLabel(correction.status) }}</v-chip>
                <strong>{{ signedMinutes(correction.minutes) }}</strong>
                <span>{{ correction.reason }}</span>
                <small v-if="correction.reviewNote">— {{ correction.reviewNote }}</small>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-tab-item>

      <!-- ===== ZESPÓŁ ===== -->
      <v-tab-item v-if="canSeeTeam">
        <div class="wt-summary" v-if="team.summary">
          <div><span class="wt-stat-icon"><v-icon size="20">mdi-account-multiple-outline</v-icon></span><small>Pracownicy</small><strong>{{ team.summary.people }}</strong></div>
          <div><span class="wt-stat-icon"><v-icon size="20">mdi-timer-sand</v-icon></span><small>Z sesji</small><strong>{{ formatMinutes(team.summary.sessionMinutes) }}</strong></div>
          <div><span class="wt-stat-icon"><v-icon size="20">mdi-pencil-outline</v-icon></span><small>Korekty</small><strong>{{ formatMinutes(team.summary.correctionMinutes) }}</strong></div>
          <div class="wt-summary-total"><span class="wt-stat-icon"><v-icon size="20">mdi-clock-check-outline</v-icon></span><small>Łączny czas</small><strong>{{ formatMinutes(team.summary.totalMinutes) }}</strong></div>
        </div>
        <div v-if="!team.rows.length && !isLoading" class="wt-empty"><span><v-icon size="28">mdi-account-search-outline</v-icon></span><strong>Brak zapisów zespołu</strong><p>Zmień zakres dat, aby zobaczyć inne wyniki.</p></div>
        <v-expansion-panels v-else accordion class="wt-days">
          <v-expansion-panel v-for="row in team.rows" :key="row.adminId">
            <v-expansion-panel-header>
              <div class="wt-day-head">
                <span class="wt-avatar">{{ initials(row.adminName || row.email) }}</span>
                <strong>{{ row.adminName || row.email }}</strong>
                <span class="wt-day-total">{{ formatMinutes(row.summary.totalMinutes) }}</span>
                <small>{{ row.summary.days }} dni z zapisem</small>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <table class="wt-table">
                <thead><tr><th>Dzień</th><th>Z sesji</th><th>Korekty</th><th>Razem</th></tr></thead>
                <tbody>
                  <tr v-for="day in row.days" :key="day.date">
                    <td>{{ formatDate(day.date) }}</td>
                    <td>{{ formatMinutes(day.sessionMinutes) }}</td>
                    <td>{{ day.correctionMinutes ? signedMinutes(day.correctionMinutes) : '—' }}</td>
                    <td><strong>{{ formatMinutes(day.totalMinutes) }}</strong></td>
                  </tr>
                </tbody>
              </table>
              <v-btn x-small outlined class="mt-2" @click="openCorrection(row.adminId)">
                <v-icon left size="16">mdi-plus</v-icon>Dopisz korektę
              </v-btn>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-tab-item>

      <!-- ===== WNIOSKI O KOREKTĘ ===== -->
      <v-tab-item v-if="canSeeTeam">
        <div v-if="!pendingCorrections.length" class="wt-empty"><span><v-icon size="28">mdi-check-decagram-outline</v-icon></span><strong>Wszystko rozpatrzone</strong><p>Nie ma wniosków czekających na decyzję.</p></div>
        <div v-for="item in pendingCorrections" :key="item.id" class="wt-request">
          <div class="wt-request-main">
            <span class="wt-avatar">{{ initials(item.adminName) }}</span>
            <strong>{{ item.adminName }}</strong>
            <span class="wt-request-date">{{ formatDate(item.date) }}</span>
            <span class="wt-request-minutes">{{ signedMinutes(item.minutes) }}</span>
          </div>
          <p class="wt-request-reason">{{ item.reason }}</p>
          <div class="wt-request-actions">
            <v-text-field v-model="reviewNotes[item.id]" dense outlined hide-details
                          placeholder="Komentarz (opcjonalnie)" class="wt-review-note" />
            <v-btn small color="success" :loading="reviewingId === item.id"
                   :disabled="item.isMine" @click="review(item, 'APPROVED')">Zatwierdź</v-btn>
            <v-btn small color="error" outlined :loading="reviewingId === item.id"
                   :disabled="item.isMine" @click="review(item, 'REJECTED')">Odrzuć</v-btn>
          </div>
          <small v-if="item.isMine" class="wt-request-hint">
            Własnej korekty nie zatwierdzisz — musi to zrobić przełożony.
          </small>
        </div>
      </v-tab-item>

      <!-- ===== USTAWIENIA ===== -->
      <v-tab-item v-if="canConfigure">
        <div class="wt-config">
          <div class="wt-config-heading"><span><v-icon size="21">mdi-cog-outline</v-icon></span><div><strong>Konfiguracja ewidencji</strong><small>Ustaw zasady rejestrowania czasu pracy w panelu.</small></div></div>
          <div class="wt-switch-card">
            <v-switch v-model="configForm.enabled" inset hide-details
                      :label="configForm.enabled ? 'Ewidencja włączona' : 'Ewidencja wyłączona'" />
            <span :class="['wt-status-dot', { active: configForm.enabled }]">{{ configForm.enabled ? 'Aktywna' : 'Nieaktywna' }}</span>
          </div>
          <p class="wt-config-hint">
            Włączenie uruchamia zapisywanie czasu pracy wszystkim pracownikom biura.
            Wymaga wcześniejszego zapisu w regulaminie pracy albo obwieszczenia
            i poinformowania pracowników na dwa tygodnie przed startem.
          </p>
          <v-text-field v-model.number="configForm.idleMinutes" type="number" min="1" max="240"
                        label="Próg pauzy (min)" outlined dense
                        hint="Po tylu minutach bez aktywności sesja jest domykana." persistent-hint />
          <v-text-field v-model.number="configForm.autoLogoutMinutes" type="number" min="5" max="480"
                        label="Auto-wylogowanie (min)" outlined dense
                        hint="Po tylu minutach bezczynności panel wylogowuje." persistent-hint />
          <v-text-field v-model.number="configForm.warnBeforeLogoutMinutes" type="number" min="1" max="30"
                        label="Ostrzeżenie przed wylogowaniem (min)" outlined dense
                        hint="Na ile minut przed wylogowaniem pokazać ostrzeżenie." persistent-hint />
          <v-text-field v-model.number="configForm.retentionMonths" type="number" min="1" max="120"
                        label="Przechowywanie ewidencji (miesiące)" outlined dense
                        hint="Po tym okresie sesje i korekty są kasowane w nocy (RODO)."
                        persistent-hint />
          <p class="wt-config-hint">
            12 miesięcy to wartość wyjściowa, nie rekomendacja prawna — ewidencja czasu pracy
            podlega przepisom o dokumentacji pracowniczej, więc okres uzgodnij z kadrami.
          </p>
          <v-btn color="primary" :loading="savingConfig" @click="saveConfig">Zapisz ustawienia</v-btn>
        </div>
      </v-tab-item>
    </v-tabs-items>

    <!-- ===== DIALOG KOREKTY ===== -->
    <v-dialog v-model="correctionDialog" max-width="520">
      <v-card class="wt-dialog-card">
        <v-card-title class="wt-dialog-title"><span><v-icon size="22">mdi-clock-edit-outline</v-icon></span><div>{{ correctionForm.adminId ? 'Korekta dla pracownika' : 'Zgłoś korektę' }}<small>Uzupełnij czas, którego nie zarejestrował panel.</small></div></v-card-title>
        <v-card-text>
          <v-text-field v-model="correctionForm.date" type="date" label="Dzień" outlined dense />
          <v-text-field v-model.number="correctionForm.minutes" type="number" label="Minuty (+ dopisuje, − odejmuje)"
                        outlined dense hint="Np. 90 za wyjazd do urzędu, −30 za omyłkowo zliczony czas."
                        persistent-hint />
          <v-textarea v-model="correctionForm.reason" label="Uzasadnienie" outlined dense rows="3"
                      counter="1000" class="mt-3" />
          <v-alert v-if="correctionError" type="error" text dense>{{ correctionError }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="correctionDialog = false">Anuluj</v-btn>
          <v-btn color="primary" :loading="savingCorrection" @click="saveCorrection">Wyślij</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { isSuperAdmin } from '@/services/permissions';
import { workTimeState } from '@/services/workTime';

export default {
  name: 'WorkTimePage',
  data() {
    const today = new Date();
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    return {
      tab: 0,
      from: this.toDayKey(first),
      to: this.toDayKey(today),
      enabled: false,
      todayMinutes: 0,
      mine: { days: [], summary: null },
      team: { rows: [], summary: null },
      config: null,
      configForm: {
        enabled: false, idleMinutes: 10, autoLogoutMinutes: 45,
        warnBeforeLogoutMinutes: 1, retentionMonths: 12,
      },
      reviewNotes: {},
      reviewingId: null,
      correctionDialog: false,
      correctionForm: { date: '', minutes: 60, reason: '', adminId: '' },
      correctionError: '',
      savingCorrection: false,
      savingConfig: false,
      loading: false,
      error: '',
    };
  },
  computed: {
    isLoading() {
      return this.loading;
    },
    canSeeTeam() {
      if (isSuperAdmin()) return true;
      try {
        return (JSON.parse(localStorage.getItem('pages') || '[]')).includes('work-time');
      } catch (e) {
        return false;
      }
    },
    canConfigure() {
      return isSuperAdmin();
    },
    myAdminId() {
      try {
        return JSON.parse(localStorage.getItem('user') || '{}').id || '';
      } catch (e) {
        return '';
      }
    },
    /** Wnioski PENDING wyciągnięte z ewidencji zespołu — backend zwraca je przy dniach. */
    pendingCorrections() {
      const items = [];
      this.team.rows.forEach((row) => {
        row.days.forEach((day) => {
          (day.corrections || []).forEach((correction) => {
            if (correction.status !== 'PENDING') return;
            items.push({
              ...correction,
              date: day.date,
              adminName: row.adminName || row.email,
              isMine: row.adminId === this.myAdminId,
            });
          });
        });
      });
      return items.sort((a, b) => (a.date < b.date ? 1 : -1));
    },
  },
  created() {
    const live = workTimeState();
    this.enabled = live.enabled;
    this.todayMinutes = live.todayMinutes;
    this.loadMine();
    if (this.canSeeTeam) this.loadTeam();
    if (this.canConfigure) this.loadConfig();
  },
  methods: {
    authConfig() {
      return { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } };
    },
    toDayKey(date) {
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${date.getFullYear()}-${month}-${day}`;
    },
    formatMinutes(minutes) {
      const value = Math.round(Number(minutes) || 0);
      const sign = value < 0 ? '−' : '';
      const abs = Math.abs(value);
      return `${sign}${Math.floor(abs / 60)} h ${String(abs % 60).padStart(2, '0')} min`;
    },
    signedMinutes(minutes) {
      return (Number(minutes) > 0 ? '+' : '') + this.formatMinutes(minutes);
    },
    formatDate(dayKey) {
      return new Date(dayKey + 'T00:00:00').toLocaleDateString('pl-PL', {
        weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
      });
    },
    formatTime(value) {
      return new Date(value).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
    },
    initials(value) {
      return String(value || '?').split(/[\s@.]+/).filter(Boolean).slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase()).join('');
    },
    endReasonLabel(reason) {
      return {
        LOGOUT: 'wylogowanie', TIMEOUT: 'bezczynność', IDLE: 'pauza (domknięta automatycznie)',
      }[reason] || (reason ? reason : 'sesja otwarta');
    },
    statusLabel(status) {
      return { PENDING: 'czeka', APPROVED: 'zatwierdzona', REJECTED: 'odrzucona' }[status] || status;
    },
    statusColor(status) {
      return { PENDING: 'orange', APPROVED: 'green', REJECTED: 'red' }[status] || 'grey';
    },
    loadActiveTab() {
      this.loadMine();
      if (this.canSeeTeam) this.loadTeam();
    },
    async loadMine() {
      this.loading = true;
      this.error = '';
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_ROOT_API}work-time/me?from=${this.from}&to=${this.to}`,
          this.authConfig(),
        );
        const data = res.data?.data || {};
        this.enabled = data.enabled === true;
        this.mine = { days: data.days || [], summary: data.summary || null };
      } catch (e) {
        this.error = e.response?.data?.message || 'Nie udało się pobrać ewidencji.';
      } finally {
        this.loading = false;
      }
    },
    async loadTeam() {
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_ROOT_API}work-time?from=${this.from}&to=${this.to}`,
          this.authConfig(),
        );
        const data = res.data?.data || {};
        this.team = { rows: data.rows || [], summary: data.summary || null };
      } catch (e) {
        this.error = e.response?.data?.message || 'Nie udało się pobrać ewidencji zespołu.';
      }
    },
    async loadConfig() {
      try {
        const res = await axios.get(`${process.env.VUE_APP_ROOT_API}work-time/config`, this.authConfig());
        this.config = res.data?.data || null;
        if (this.config) this.configForm = { ...this.config };
      } catch (e) {
        // Brak dostępu do konfiguracji nie jest błędem strony.
      }
    },
    async saveConfig() {
      this.savingConfig = true;
      this.error = '';
      try {
        const res = await axios.patch(
          `${process.env.VUE_APP_ROOT_API}work-time/config`, this.configForm, this.authConfig(),
        );
        this.config = res.data?.data || this.configForm;
        this.configForm = { ...this.config };
        this.enabled = this.config.enabled;
      } catch (e) {
        this.error = e.response?.data?.message || 'Nie udało się zapisać ustawień.';
      } finally {
        this.savingConfig = false;
      }
    },
    openCorrection(adminId = '') {
      this.correctionForm = { date: this.toDayKey(new Date()), minutes: 60, reason: '', adminId };
      this.correctionError = '';
      this.correctionDialog = true;
    },
    async saveCorrection() {
      this.correctionError = '';
      if (!this.correctionForm.minutes) {
        this.correctionError = 'Podaj liczbę minut — korekta zerowa nic nie zmienia.';
        return;
      }
      if (!this.correctionForm.reason.trim()) {
        this.correctionError = 'Uzasadnienie jest wymagane.';
        return;
      }
      this.savingCorrection = true;
      try {
        const payload = {
          date: this.correctionForm.date,
          minutes: this.correctionForm.minutes,
          reason: this.correctionForm.reason.trim(),
        };
        if (this.correctionForm.adminId) payload.adminId = this.correctionForm.adminId;
        await axios.post(`${process.env.VUE_APP_ROOT_API}work-time/corrections`, payload, this.authConfig());
        this.correctionDialog = false;
        this.loadActiveTab();
      } catch (e) {
        this.correctionError = e.response?.data?.message || 'Nie udało się zapisać korekty.';
      } finally {
        this.savingCorrection = false;
      }
    },
    async review(item, status) {
      this.reviewingId = item.id;
      this.error = '';
      try {
        await axios.patch(
          `${process.env.VUE_APP_ROOT_API}work-time/corrections/${item.id}`,
          { status, reviewNote: this.reviewNotes[item.id] || undefined },
          this.authConfig(),
        );
        this.$set(this.reviewNotes, item.id, '');
        await this.loadTeam();
      } catch (e) {
        this.error = e.response?.data?.message || 'Nie udało się zapisać decyzji.';
      } finally {
        this.reviewingId = null;
      }
    },
  },
};
</script>

<style scoped>
.wt-page { --wt-blue: #5271ff; --wt-surface: #182331; --wt-surface-soft: #1d2a39; --wt-border: rgba(151, 170, 198, .15); padding: 22px 24px 48px; max-width: 1500px; margin: 0 auto; }
.wt-hero { position: relative; display: flex; flex-wrap: wrap; gap: 20px; justify-content: space-between; align-items: center; padding: 22px 24px; margin-bottom: 16px; overflow: hidden; border: 1px solid var(--wt-border); border-radius: 18px; background: linear-gradient(125deg, #1d2a3a 0%, #172332 68%, rgba(63,81,181,.35) 145%); box-shadow: 0 16px 38px rgba(4, 10, 18, .18); }
.wt-hero::after { content: ''; position: absolute; width: 220px; height: 220px; right: -85px; top: -125px; border-radius: 50%; background: rgba(82, 113, 255, .13); pointer-events: none; }
.wt-heading { position: relative; z-index: 1; display: flex; gap: 16px; align-items: center; }
.wt-heading-icon { width: 50px; height: 50px; flex: 0 0 50px; border-radius: 15px; display: grid; place-items: center; background: linear-gradient(145deg, #5d79ff, #3d55da); color: white; box-shadow: 0 9px 22px rgba(65, 86, 220, .34); }
.wt-heading-icon .v-icon { color: white !important; }
.wt-eyebrow { color: #91a4c0; font-size: 10px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; }
.wt-heading h1 { color: #f5f7fb; font-size: 25px; font-weight: 750; letter-spacing: -.025em; line-height: 1.2; margin: 3px 0 4px; }
.wt-heading p { margin: 0; color: #a6b4c8; font-size: 13px; }
.wt-hero-actions { position: relative; z-index: 1; display: flex; gap: 10px; align-items: stretch; }
.wt-context-card { min-width: 150px; display: grid; grid-template-columns: auto 1fr; gap: 1px 7px; align-items: center; padding: 9px 13px; border: 1px solid var(--wt-border); border-radius: 13px; background: rgba(8, 17, 29, .25); }
.wt-context-card span { grid-row: 1; color: #91a4c0; font-size: 11px; display: flex; align-items: center; gap: 4px; }
.wt-context-card strong { grid-column: 1 / -1; color: white; font-size: 18px; letter-spacing: -.02em; }
.wt-context-card small { grid-column: 1 / -1; color: #8798af; font-size: 10px; }
.wt-refresh { display: flex; align-items: center; gap: 7px; padding: 0 14px; border-radius: 13px; border: 1px solid var(--wt-border); background: rgba(8, 17, 29, .18); color: #dce4ef; font-size: 12px; font-weight: 600; transition: .18s ease; }
.wt-refresh:hover { border-color: rgba(111, 137, 255, .55); background: rgba(82, 113, 255, .1); }
.wt-refresh:disabled { opacity: .6; }
.is-spinning { animation: wt-spin 1s linear infinite; }
@keyframes wt-spin { to { transform: rotate(360deg); } }
.wt-alert { margin-bottom: 14px; border-radius: 12px !important; }
.wt-toolbar { display: flex; gap: 14px; justify-content: space-between; align-items: center; margin-bottom: 16px; padding: 13px 15px; border: 1px solid var(--wt-border); border-radius: 15px; background: var(--wt-surface); }
.wt-range { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.wt-toolbar-label { display: inline-flex; gap: 6px; align-items: center; margin-right: 2px; color: #aab8ca; font-size: 12px; font-weight: 600; }
.wt-date { max-width: 160px; }
.wt-range-separator { color: #617087; }
.wt-show-btn, .wt-correction-btn { height: 40px !important; border-radius: 10px !important; text-transform: none !important; font-weight: 650 !important; letter-spacing: 0 !important; }
.wt-tabs { margin-bottom: 0; border: 1px solid var(--wt-border); border-bottom: 0; border-radius: 15px 15px 0 0; overflow: hidden; background: #15202d !important; }
.wt-tabs ::v-deep .v-tab { min-height: 54px; color: #91a0b4 !important; text-transform: none; letter-spacing: 0; font-size: 13px; font-weight: 600; }
.wt-tabs ::v-deep .v-tab--active { color: #eef2ff !important; background: rgba(82, 113, 255, .09); }
.wt-badge { display: inline-grid; place-items: center; min-width: 18px; height: 18px; margin-left: 6px; padding: 0 5px; border-radius: 9px; background: #fb8c00; color: #fff; font-size: 11px; }
.wt-tab-body { min-height: 300px; padding: 20px; border: 1px solid var(--wt-border); border-radius: 0 0 15px 15px; background: #111b27 !important; }
.wt-summary { display: grid; grid-template-columns: repeat(4, minmax(150px, 1fr)); gap: 12px; margin: 0 0 18px; }
.wt-summary > div { position: relative; min-height: 91px; padding: 16px 14px 14px 54px; border: 1px solid var(--wt-border); border-radius: 13px; background: var(--wt-surface); display: flex; flex-direction: column; justify-content: center; }
.wt-summary small { color: #8f9eb2; font-size: 11px; font-weight: 600; }
.wt-summary strong { color: #f3f6fb; font-size: 19px; letter-spacing: -.025em; }
.wt-stat-icon { position: absolute; left: 14px; top: 50%; width: 31px; height: 31px; display: grid; place-items: center; transform: translateY(-50%); border-radius: 9px; background: rgba(111, 137, 255, .11); }
.wt-stat-icon .v-icon { color: #7e97ff !important; }
.wt-summary-total { border-color: rgba(82, 113, 255, .35) !important; background: linear-gradient(145deg, rgba(71, 94, 208, .28), rgba(33, 49, 73, .95)) !important; }
.wt-summary-total .wt-stat-icon { background: rgba(82, 113, 255, .24); }
.wt-empty { min-height: 220px; display: flex; flex-direction: column; justify-content: center; align-items: center; margin: 0; color: #8796a9; text-align: center; }
.wt-empty > span { width: 52px; height: 52px; display: grid; place-items: center; margin-bottom: 12px; border-radius: 50%; background: rgba(111, 137, 255, .09); }
.wt-empty strong { color: #cad3df; font-size: 14px; }
.wt-empty p { margin: 4px 0 0; font-size: 12px; }
.wt-days { margin-bottom: 20px; }
.wt-days ::v-deep .v-expansion-panel { margin-bottom: 8px; border: 1px solid var(--wt-border); border-radius: 12px !important; overflow: hidden; background: var(--wt-surface) !important; }
.wt-days ::v-deep .v-expansion-panel::before { box-shadow: none; }
.wt-days ::v-deep .v-expansion-panel-header { min-height: 62px; padding: 12px 16px; }
.wt-day-head { width: 100%; display: flex; gap: 11px; align-items: center; flex-wrap: wrap; }
.wt-day-head strong { color: #e8edf4; font-size: 13px; }
.wt-day-head small { color: #8392a6; font-size: 11px; }
.wt-day-calendar { width: 31px; height: 31px; display: grid; place-items: center; border-radius: 9px; background: rgba(111, 137, 255, .1); }
.wt-day-total { margin-left: auto; padding: 5px 9px; border-radius: 8px; background: rgba(111, 137, 255, .1); color: #9eb0ff; font-size: 12px; font-weight: 700; }
.wt-avatar { width: 31px; height: 31px; display: inline-grid; place-items: center; flex: 0 0 31px; border-radius: 9px; background: linear-gradient(145deg, #526fe7, #344dbb); color: white; font-size: 10px; font-weight: 750; }
.wt-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.wt-table th { color: #8090a5; text-align: left; font-size: 10px; font-weight: 700; letter-spacing: .05em; text-transform: uppercase; padding: 7px 10px; }
.wt-table td { padding: 9px 10px; border-top: 1px solid var(--wt-border); color: #cbd4df; }
.wt-correction { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-top: 10px; padding: 10px 12px; border-radius: 9px; background: rgba(8, 16, 26, .22); font-size: 12px; }
.wt-request { padding: 16px; border: 1px solid var(--wt-border); border-radius: 13px; background: var(--wt-surface); margin-bottom: 10px; transition: border-color .18s ease; }
.wt-request:hover { border-color: rgba(111, 137, 255, .3); }
.wt-request-main { display: flex; gap: 12px; align-items: baseline; flex-wrap: wrap; }
.wt-request-date { opacity: .75; font-size: 13px; }
.wt-request-minutes { margin-left: auto; padding: 5px 9px; border-radius: 8px; background: rgba(255, 152, 0, .1); color: #ffb74d; font-weight: 700; }
.wt-request-reason { margin: 10px 0 12px 43px; color: #aeb9c8; font-size: 13px; }
.wt-request-actions { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.wt-review-note { max-width: 320px; }
.wt-request-hint { display: block; margin-top: 6px; opacity: .7; }
.wt-config { max-width: 600px; display: flex; flex-direction: column; gap: 14px; }
.wt-config-heading { display: flex; align-items: center; gap: 11px; padding-bottom: 13px; border-bottom: 1px solid var(--wt-border); }
.wt-config-heading > span { width: 38px; height: 38px; display: grid; place-items: center; border-radius: 10px; background: rgba(111, 137, 255, .1); }
.wt-config-heading div { display: flex; flex-direction: column; }
.wt-config-heading strong { color: #edf1f7; font-size: 15px; }
.wt-config-heading small { color: #8998ac; font-size: 11px; }
.wt-switch-card { display: flex; justify-content: space-between; align-items: center; padding: 8px 13px 8px 2px; border: 1px solid var(--wt-border); border-radius: 11px; background: var(--wt-surface); }
.wt-status-dot { padding: 5px 9px; border-radius: 20px; background: rgba(145, 158, 171, .11); color: #9da8b5; font-size: 10px; font-weight: 700; text-transform: uppercase; }
.wt-status-dot.active { background: rgba(76, 175, 80, .13); color: #74d478; }
.wt-config-hint { color: #8493a6; font-size: 11px; line-height: 1.6; margin: -7px 2px 1px; }
.wt-dialog-card { overflow: hidden; border: 1px solid var(--wt-border); border-radius: 17px !important; }
.wt-dialog-title { display: flex; gap: 12px; align-items: center; padding: 20px 22px !important; border-bottom: 1px solid var(--wt-border); }
.wt-dialog-title > span { width: 40px; height: 40px; display: grid; place-items: center; border-radius: 11px; background: rgba(111, 137, 255, .12); }
.wt-dialog-title > div { display: flex; flex-direction: column; color: #f1f4f8; font-size: 17px; line-height: 1.3; }
.wt-dialog-title small { color: #8998aa; font-size: 11px; font-weight: 400; }
@media (max-width: 900px) {
  .wt-page { padding: 16px 14px 36px; }
  .wt-summary { grid-template-columns: repeat(2, minmax(140px, 1fr)); }
  .wt-toolbar { align-items: stretch; flex-direction: column; }
  .wt-correction-btn { align-self: flex-start; }
}
@media (max-width: 600px) {
  .wt-hero { padding: 18px; }
  .wt-heading { align-items: flex-start; }
  .wt-heading h1 { font-size: 21px; }
  .wt-heading p { max-width: 240px; }
  .wt-hero-actions { width: 100%; }
  .wt-context-card { flex: 1; }
  .wt-summary { grid-template-columns: 1fr; }
  .wt-range { align-items: stretch; flex-direction: column; }
  .wt-date { max-width: none; width: 100%; }
  .wt-range-separator, .wt-toolbar-label { display: none; }
  .wt-show-btn, .wt-correction-btn { width: 100%; }
  .wt-tab-body { padding: 12px; }
  .wt-table { min-width: 520px; }
  .wt-days ::v-deep .v-expansion-panel-content__wrap { overflow-x: auto; padding: 0 10px 14px; }
  .wt-day-head small { width: 100%; padding-left: 42px; }
  .wt-request-reason { margin-left: 0; }
}
</style>
